import React, { useState, useEffect, useCallback } from 'react'
import { Box, Typography, Chip, Snackbar, Alert, TextField, IconButton } from '@mui/material'
import { Check as CheckIcon, Close as CloseIcon } from '@mui/icons-material'
import { useMutation } from '@apollo/client'
import { useSelector } from 'react-redux'
import { fromJS } from 'immutable'
import { selectAuth } from 'services/auth/redux/selectors'
import { selectBrooklynServerUrl } from 'services/config/redux/selectors'
import { createContent, removeContent } from 'services/content'
import { CONTENT_REFERENCE_TYPE_COURSE_MODULE } from 'services/content-references/types'
import MuiButton from 'gui/components/common/MuiButton'
import MuiTextField from 'gui/components/common/MuiTextField'
import Link from 'gui/components/link'
import { LINK_TARGET_BLANK } from 'gui/components/link/targets'
import { GET_COURSE_CONTENT, UPDATE_CONTENT_REFERENCES_SORT_ORDER } from './queries'
import { CREATE_CONTENT_REFERENCE } from './content-references/queries'
import type { CourseModuleReference, SnackbarState } from './types'
import './course-modules-section.scss'

interface CourseModulesSectionProps {
  /** Array of course module references */
  modules: CourseModuleReference[]
  /** Whether the section is disabled (view-only mode) */
  disabled: boolean
  /** Language of the course */
  courseLanguage: string
  /** Content ID of the course */
  courseContentId: number
  /** Content type of the course */
  courseContentType: string
  /** ID of the course */
  courseId: string
  /** Callback when dirty state changes */
  onDirtyChange?: (isDirty: boolean) => void
  /** Callback to get the save handler */
  onSaveHandlerReady?: (saveHandler: () => Promise<void>) => void
  /** Reset trigger counter - incremented by parent to trigger reset */
  onResetTriggered?: number
}

/**
 * Course Modules Section Component
 * Displays list of Course Modules linked to a Course
 */
function CourseModulesSection (props: CourseModulesSectionProps) {
  const {
    modules: serverModules = [],
    disabled = false,
    courseLanguage,
    courseContentId,
    courseContentType,
    courseId,
    onDirtyChange,
    onSaveHandlerReady,
    onResetTriggered = 0,
  } = props

  const auth = useSelector(selectAuth)
  const { url: brooklynUrl } = useSelector(selectBrooklynServerUrl)

  // State for add course module form
  const [isAddingModule, setIsAddingModule] = useState<boolean>(false)
  const [newModuleTitle, setNewModuleTitle] = useState<string>('')
  const [newModuleDescription, setNewModuleDescription] = useState<string>('')
  const [isSaving, setIsSaving] = useState<boolean>(false)

  // State for tracking edited sort keys (currently being typed, not confirmed)
  // Map of contentId -> edited sort key value
  const [editedSortKeys, setEditedSortKeys] = useState<Record<number, string>>({})

  // State for locally reordered modules (confirmed by user, waiting to be saved to server)
  // Array of modules in their new order
  const [locallyReorderedModules, setLocallyReorderedModules] = useState<
    CourseModuleReference[] | null
  >(null)

  // State for tracking removals
  // id of course module showing confirmation dialog
  const [modulePendingRemoval, setModulePendingRemoval] = useState<string | null>(null)
  // Set of ids confirmed for deletion (triggers dirty state, hides from UI)
  const [modulesToDelete, setModulesToDelete] = useState<Set<string>>(new Set())

  // Snackbar state for success/error messages
  const [snackbar, setSnackbar] = useState<SnackbarState>({
    open: false,
    message: '',
    severity: 'success',
  })

  // Notify parent when dirty state changes (based on locallyReorderedModules or modulesToDelete)
  useEffect(() => {
    const isDirty = locallyReorderedModules !== null || modulesToDelete.size > 0
    if (onDirtyChange) {
      onDirtyChange(isDirty)
    }
  }, [locallyReorderedModules, modulesToDelete, onDirtyChange])

  // Handle reset trigger from parent (Cancel button)
  useEffect(() => {
    if (onResetTriggered > 0) {
      setEditedSortKeys({})
      setLocallyReorderedModules(null)
      setModulesToDelete(new Set())
      setModulePendingRemoval(null)
    }
  }, [onResetTriggered])

  // Mutation for creating content reference
  const [createContentReference] = useMutation(CREATE_CONTENT_REFERENCE, {
    refetchQueries: [{ query: GET_COURSE_CONTENT, variables: { id: courseId } }],
    awaitRefetchQueries: true,
  })

  // Mutation for updating content reference sort order
  const [updateContentReferencesSortOrder] = useMutation(UPDATE_CONTENT_REFERENCES_SORT_ORDER, {
    refetchQueries: [{ query: GET_COURSE_CONTENT, variables: { id: courseId } }],
    awaitRefetchQueries: true,
  })

  // Save handler to be called by parent's Save button
  const handleSave = useCallback(async (): Promise<void> => {
    const hasReorder = locallyReorderedModules !== null
    const hasDeletions = modulesToDelete.size > 0

    if (!hasReorder && !hasDeletions) {
      return
    }

    try {
      // Step 1: Build the list of course modules to save
      // Start with reordered course modules if user reordered, otherwise use original server data
      let modulesToSave = locallyReorderedModules || serverModules

      // If there are deletions, filter them out from the list
      if (hasDeletions) {
        modulesToSave = modulesToSave.filter((ref) => {
          const module = ref.contentFrom?.[0] || ref.contentFrom
          return !modulesToDelete.has(module?.id)
        })
      }

      // Step 2: Update content references with clean sequential sortKeys
      // This single mutation handles both reordering AND deletions
      await updateContentReferencesSortOrder({
        variables: {
          contentReferences: modulesToSave.map((ref, index) => {
            const module = ref.contentFrom?.[0] || ref.contentFrom
            return {
              fromContentContentId: module.contentId,
              fromContentContentType: module.contentType,
              toContentContentId: courseContentId,
              toContentContentType: courseContentType,
              type: CONTENT_REFERENCE_TYPE_COURSE_MODULE,
              sortKey: index, // Clean sequential sortKeys: 0, 1, 2, 3...
            }
          }),
          deleteOmittedReferences: true, // Removes content refs for deleted course modules
        },
      })

      // Step 3: Delete the course module content itself (in parallel)
      if (hasDeletions) {
        const deletionPromises = Array.from(modulesToDelete).map((id) =>
          removeContent({
            auth,
            server: brooklynUrl,
            contentId: id,
          }),
        )

        await Promise.all(deletionPromises)
      }

      // Clear all pending states after successful save
      setLocallyReorderedModules(null)
      setModulesToDelete(new Set())
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error'
      setSnackbar({
        open: true,
        message: `Error saving changes: ${errorMessage}`,
        severity: 'error',
      })
      // Re-throw so parent knows save failed
      throw error
    }
  }, [
    locallyReorderedModules,
    modulesToDelete,
    serverModules,
    updateContentReferencesSortOrder,
    courseContentId,
    courseContentType,
    removeContent,
    auth,
    brooklynUrl,
  ])

  // Provide save handler to parent
  useEffect(() => {
    if (onSaveHandlerReady) {
      onSaveHandlerReady(handleSave)
    }
  }, [onSaveHandlerReady, handleSave])

  // Handle snackbar close
  const handleSnackbarClose = () => {
    setSnackbar((prev) => ({ ...prev, open: false }))
  }

  // Handle sort key change
  const handleSortKeyChange = (contentId: number, value: string) => {
    setEditedSortKeys((prev) => ({
      ...prev,
      [contentId]: value,
    }))
  }

  // Handle confirm sort key change - confirms the reorder locally (doesn't save yet)
  const handleConfirmSortKey = (contentId: number) => {
    const newPositionStr = editedSortKeys[contentId]
    const newPosition = parseInt(newPositionStr, 10) - 1 // Convert from 1-indexed to 0-indexed

    // Validate the new position
    if (Number.isNaN(newPosition) || newPosition < 0 || newPosition >= serverModules.length) {
      setSnackbar({
        open: true,
        message: `Invalid position. Please enter a number between 1 and ${serverModules.length}`,
        severity: 'error',
      })
      return
    }

    // Use locally reordered course modules if available, otherwise use original server course modules
    const currentModules = locallyReorderedModules || serverModules

    // Find current position of the course module
    // IMPORTANT: locallyReorderedModules is already in correct order from previous splice()
    // Only sort the original serverModules by their sortKey from the server
    const sortedModules = locallyReorderedModules
      ? [...currentModules] // Already ordered correctly - don't sort!
      : [...currentModules].sort((a, b) => {
          const sortKeyA = a.sortKey || 0
          const sortKeyB = b.sortKey || 0
          return sortKeyA - sortKeyB
        })

    const currentPosition = sortedModules.findIndex((module) => {
      const moduleContent = module.contentFrom?.[0] || module.contentFrom
      return moduleContent?.contentId === contentId
    })

    if (currentPosition === -1) {
      setSnackbar({
        open: true,
        message: 'Error: Could not find course module',
        severity: 'error',
      })
      return
    }

    // If position hasn't changed, just clear the edit
    if (currentPosition === newPosition) {
      setEditedSortKeys((prev) => {
        const updated = { ...prev }
        delete updated[contentId]
        return updated
      })
      return
    }

    // Reorder the array using splice (Page Manager pattern)
    const reorderedModules = [...sortedModules]
    const [movedModule] = reorderedModules.splice(currentPosition, 1)
    reorderedModules.splice(newPosition, 0, movedModule)

    // Store the reordered modules locally (this triggers dirty state)
    setLocallyReorderedModules(reorderedModules)

    // Clear the edit field for this step
    setEditedSortKeys((prev) => {
      const updated = { ...prev }
      delete updated[contentId]
      return updated
    })
  }

  // Handle cancel sort key change
  const handleCancelSortKey = (contentId: number) => {
    // Clear the edited state
    setEditedSortKeys((prev) => {
      const updated = { ...prev }
      delete updated[contentId]
      return updated
    })
  }

  // Handle clicking Remove button (shows confirmation)
  const handleRemoveClick = (id: string) => {
    setModulePendingRemoval(id)
  }

  // Handle confirming removal
  const handleConfirmRemove = (id: string) => {
    // Add to deletion set (triggers dirty state, hides from UI)
    setModulesToDelete((prev) => new Set(prev).add(id))
    // Clear pending removal state
    setModulePendingRemoval(null)
  }

  // Handle canceling removal
  const handleCancelRemove = () => {
    setModulePendingRemoval(null)
  }

  // Handle opening the add form
  const handleAddClick = () => {
    setIsAddingModule(true)
  }

  // Handle canceling the add form
  const handleCancelAdd = () => {
    setIsAddingModule(false)
    setNewModuleTitle('')
    setNewModuleDescription('')
  }

  // Calculate the next sort key (one larger than the largest existing sort key)
  const getNextSortKey = (): number => {
    if (!serverModules || serverModules.length === 0) {
      return 0
    }
    const maxSortKey = Math.max(...serverModules.map((module) => module.sortKey || 0))
    return maxSortKey + 1
  }

  // Handle saving the new course module
  const handleSaveModule = async () => {
    if (!newModuleTitle.trim()) {
      return
    }

    setIsSaving(true)

    try {
      // Step 1: Create the course module content using REST API
      // Note: createContent service requires Immutable.js Map
      // This will be removed when we convert the service layer to TypeScript
      const contentData = fromJS({
        contentType: 'course-module',
        language: courseLanguage,
        primaryLanguage: courseLanguage,
        title: newModuleTitle.trim(),
        description: newModuleDescription.trim() || '',
      })

      const createdContent = await createContent({
        auth,
        server: brooklynUrl,
        data: contentData,
        generatePath: true,
      })

      // Service returns Immutable.js Map
      const newContentId = createdContent.get('contentId')
      const newContentType = createdContent.get('contentType')

      // Step 2: Create the content reference linking the course module to the course
      const sortKey = getNextSortKey()

      await createContentReference({
        variables: {
          contentReference: {
            fromContentContentId: newContentId,
            fromContentContentType: newContentType,
            toContentContentId: courseContentId,
            toContentContentType: courseContentType,
            type: CONTENT_REFERENCE_TYPE_COURSE_MODULE,
            sortKey,
          },
        },
      })

      // Reset form
      setIsAddingModule(false)
      setNewModuleTitle('')
      setNewModuleDescription('')

      // Show success message
      setSnackbar({
        open: true,
        message: `Course Module "${newModuleTitle}" created successfully!`,
        severity: 'success',
      })
    } catch (error) {
      // Show error message
      const errorMessage = error instanceof Error ? error.message : 'Unknown error'
      setSnackbar({
        open: true,
        message: `Error creating course module: ${errorMessage}`,
        severity: 'error',
      })
    } finally {
      setIsSaving(false)
    }
  }

  // Extract add button/form JSX to avoid duplication
  const addModuleSection = !disabled ? (
    <Box className="course-modules-section__add">
      {!isAddingModule ? (
        <MuiButton
          className="course-modules-section__add-button"
          variant="contained"
          color="primary"
          onClick={handleAddClick}
        >
          Add Course Module
        </MuiButton>
      ) : (
        <Box className="course-modules-section__add-form">
          <Typography variant="h6" gutterBottom>
            Add New Course Module
          </Typography>

          <Box className="course-modules-section__form-field">
            <MuiTextField
              value={newModuleTitle}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setNewModuleTitle(e.target.value)
              }
              placeholder="Enter course module title"
            />
          </Box>

          <Box className="course-modules-section__form-field">
            <MuiTextField
              value={newModuleDescription}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setNewModuleDescription(e.target.value)
              }
              placeholder="Enter course module description"
            />
          </Box>

          <Box className="course-modules-section__form-actions">
            <MuiButton
              variant="contained"
              color="primary"
              className=""
              onClick={handleSaveModule}
              disabled={!newModuleTitle.trim() || isSaving}
            >
              {isSaving ? 'Saving...' : 'Save'}
            </MuiButton>
            <MuiButton
              variant="outlined"
              className=""
              onClick={handleCancelAdd}
              disabled={isSaving}
            >
              Cancel
            </MuiButton>
          </Box>
        </Box>
      )}
    </Box>
  ) : null

  if (!serverModules || serverModules.length === 0) {
    return (
      <Box className="course-modules-section">
        <Typography variant="body2" color="textSecondary">
          No Course Modules linked yet. Add Course Modules to build your course.
        </Typography>
        {addModuleSection}

        {/* Snackbar for success/error messages */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={handleSnackbarClose}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        >
          <Alert
            onClose={handleSnackbarClose}
            severity={snackbar.severity}
            className="course-modules-section__snackbar-alert"
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Box>
    )
  }

  // Use locally reordered modules if available, otherwise use original server modules
  const currentModules = locallyReorderedModules || serverModules

  // Filter out modules marked for deletion
  const visibleModules = currentModules.filter((moduleRef) => {
    const module = moduleRef.contentFrom?.[0] || moduleRef.contentFrom
    return !modulesToDelete.has(module?.id)
  })

  // Sort by sortKey ONLY if we're using the original serverModules
  // If locallyReorderedModules exists, it's already in the correct order!
  const sortedModules = locallyReorderedModules
    ? [...visibleModules] // Use as-is, already reordered
    : [...visibleModules].sort((a, b) => {
        const sortKeyA = a.sortKey || 0
        const sortKeyB = b.sortKey || 0
        return sortKeyA - sortKeyB
      })

  return (
    <Box className="modules-section">
      <Box className="course-course-modules-section__list">
        {sortedModules.map((moduleRef, index) => {
          // Handle both mock and real data structure
          const module = moduleRef.contentFrom?.[0] || moduleRef.contentFrom || {}
          const moduleContentId = module.contentId
          const moduleId = module.id
          const moduleTitle = module.title || 'Untitled Module'
          // Display position is always index + 1 (1-indexed), regardless of actual sortKey
          const displayPosition = index + 1

          // Count practice and other content for this course module
          const moduleContent = module.contentReferencesTo || []
          const practiceCount = moduleContent.filter(
            (ref) => ref.contentReferenceType?.type === 'course-module-practice',
          ).length
          const otherCount = moduleContent.filter(
            (ref) => ref.contentReferenceType?.type === 'course-module-other',
          ).length

          return (
            <Box key={moduleContentId || index} className="course-course-modules-section__item">
              <Box className="course-modules-section__item-main">
                <Box className="course-modules-section__item-info">
                  <Box className="course-modules-section__item-position">
                    <Box className="course-modules-section__position-input-wrapper">
                      <TextField
                        value={
                          editedSortKeys[moduleContentId] !== undefined
                            ? editedSortKeys[moduleContentId]
                            : displayPosition
                        }
                        onChange={(e) => handleSortKeyChange(moduleContentId, e.target.value)}
                        disabled={disabled}
                        type="number"
                        size="small"
                        className="course-modules-section__position-input"
                      />
                      {editedSortKeys[moduleContentId] !== undefined && (
                        <Box className="course-modules-section__position-actions">
                          <IconButton
                            size="small"
                            color="success"
                            onClick={() => handleConfirmSortKey(moduleContentId)}
                            className="course-modules-section__position-action-button course-modules-section__position-action-button--confirm"
                          >
                            <CheckIcon className="course-modules-section__position-action-icon" />
                          </IconButton>
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleCancelSortKey(moduleContentId)}
                            className="course-modules-section__position-action-button course-modules-section__position-action-button--cancel"
                          >
                            <CloseIcon className="course-modules-section__position-action-icon" />
                          </IconButton>
                        </Box>
                      )}
                    </Box>
                  </Box>
                  <Box className="course-modules-section__item-details">
                    <Typography variant="subtitle1" className="course-modules-section__item-title">
                      {moduleTitle}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      ID: {moduleContentId}
                    </Typography>
                    <Box className="course-modules-section__item-content-summary">
                      <Chip
                        label={`${practiceCount} ${practiceCount === 1 ? 'Practice' : 'Practices'}`}
                        size="small"
                        color="success"
                        variant="outlined"
                      />
                      <Chip
                        label={`${otherCount} Other`}
                        size="small"
                        color="info"
                        variant="outlined"
                      />
                    </Box>
                  </Box>
                </Box>
                <Box className="course-modules-section__item-actions">
                  {moduleId && modulePendingRemoval !== moduleId && (
                    <Link
                      href={`/content/${module.id || moduleContentId}`}
                      target={LINK_TARGET_BLANK}
                      absolute
                    >
                      <MuiButton size="small" variant="outlined" className="" onClick={() => {}}>
                        View
                      </MuiButton>
                    </Link>
                  )}
                  {!disabled && moduleId && modulePendingRemoval !== moduleId && (
                    <MuiButton
                      size="small"
                      color="error"
                      variant="outlined"
                      className=""
                      onClick={() => handleRemoveClick(moduleId)}
                    >
                      Remove
                    </MuiButton>
                  )}
                  {!disabled && moduleId && modulePendingRemoval === moduleId && (
                    <Box className="course-modules-section__remove-confirmation">
                      <Typography
                        variant="caption"
                        color="error"
                        className="course-modules-section__remove-confirmation-message"
                      >
                        Are you sure you want to DELETE this course module and its contents?
                      </Typography>
                      <Box className="course-modules-section__remove-confirmation-actions">
                        <MuiButton
                          size="small"
                          color="error"
                          variant="contained"
                          className=""
                          onClick={() => handleConfirmRemove(moduleId)}
                        >
                          Confirm Delete
                        </MuiButton>
                        <MuiButton
                          size="small"
                          variant="outlined"
                          className=""
                          onClick={handleCancelRemove}
                        >
                          Cancel
                        </MuiButton>
                      </Box>
                    </Box>
                  )}
                </Box>
              </Box>
            </Box>
          )
        })}
      </Box>

      {addModuleSection}

      {/* Snackbar for success/error messages */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbar.severity}
          className="course-modules-section__snackbar-alert"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  )
}

export default CourseModulesSection
